import { ACCESSORIES_PROPERTIES, DEVICE_EVENT_TYPES } from '@/constants/device';
import { useQueries } from '@tanstack/react-query';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const useGetLatestUserEventsMultiple = ({
  useruuid,
  deviceuuids = [],
  accessories = [],
  ...options
}) => {
  const trans = useSelector(translator);
  const userEventsApi = useSelector((state) => state.apis.userEventsApi);
  const { enabled: optionsEnabled = true, ...queryOptions } = options || {};

  const queryResults = useQueries({
    queries: deviceuuids.map((singleDeviceUuid) => {
      const deviceType = accessories.find(
        (a) => a[ACCESSORIES_PROPERTIES.DEVICE_UUID] === singleDeviceUuid
      )?.[ACCESSORIES_PROPERTIES.DEVICE_TYPE];
      const envEventTypes = deviceType ? DEVICE_EVENT_TYPES[deviceType] || [] : [];

      return {
        queryKey: ['getLatestUserEvents', useruuid, singleDeviceUuid],
        queryFn: () =>
          new Promise((resolve, reject) => {
            userEventsApi.getLatestUserEvents(
              useruuid,
              { deviceuuid: [singleDeviceUuid], eventtypes: envEventTypes },
              apiCallback({
                translator: trans,
                successCallback: (data) => resolve(data['user_events'] || []),
                errorCallback: () => reject([]),
                failCallback: () => reject([]),
              })
            );
          }),
        enabled: !!useruuid && !isEmpty(deviceuuids) && optionsEnabled,
        ...queryOptions,
      };
    }),
  });

  const data = useMemo(
    () => queryResults.flatMap((queryResult) => queryResult.data || []),
    [queryResults]
  );

  const { isFetching, isLoading, isError, error } = useMemo(() => {
    const isFetching = queryResults.some((queryResult) => queryResult.isFetching);
    const isLoading = queryResults.some((queryResult) => queryResult.isLoading);
    const isError = queryResults.some((queryResult) => queryResult.isError);
    const errorResult = queryResults.find((queryResult) => queryResult.error);
    const error = errorResult?.error;
    return { isFetching, isLoading, isError, error };
  }, [queryResults]);

  const refetch = () => Promise.all(queryResults.map((queryResult) => queryResult.refetch()));

  return {
    data,
    isFetching,
    isLoading,
    isError,
    error,
    refetch,
    queries: queryResults,
  };
};

export default useGetLatestUserEventsMultiple;
